import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { ViewerClient } from "../client/ViewerClient";
import type { Catalogs } from "../shared/api-contract";

interface ViewerContextValue {
  client: ViewerClient;
  catalogs: Catalogs;
}

const ViewerContext = createContext<ViewerContextValue | null>(null);

/**
 * Stays mounted for the whole app lifetime (value becomes non-null once the
 * iframe handshake + catalogs resolve). Keeping the provider element stable
 * means the viewer <iframe> never remounts/reloads when we go "ready".
 */
export function ViewerProvider({
  client,
  catalogs,
  children,
}: {
  client: ViewerClient | null;
  catalogs: Catalogs | null;
  children: ReactNode;
}) {
  const value = useMemo(
    () => (client && catalogs ? { client, catalogs } : null),
    [client, catalogs],
  );
  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): ViewerContextValue {
  const ctx = useContext(ViewerContext);
  if (!ctx) throw new Error("useViewer must be used within a ready ViewerProvider");
  return ctx;
}
